import React, { useContext, useEffect, useState } from 'react';
import {
  Field,
  SitecoreContext,
  withSitecoreContext,
  RichText,
  WithSitecoreContextProps,
} from '@sitecore-jss/sitecore-jss-react';

import './index.scss';
import { postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { APIWebstoreContext } from '../../context/globalContext';
import LoadingSection from '../LoadingSection/LoadingSection';
import { OrderResponse } from '../PlaceOrderProcess/PlaceOrderProcess';
import { Navigate } from 'react-router-dom';
import { MONOGRAM_VERIFONE_SUBSCRIPTION_SUFFIX } from '../../utils/constants';

type ExternalPaymentProcessingProps = WithSitecoreContextProps & {
  fields: {
    PaymentProcessMessage: Field<string>;
    PaymentProcessErrorMessage: Field<string>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
};

const ExternalPaymentProcessing = ({
  fields,
  sitecoreContext,
}: ExternalPaymentProcessingProps): React.JSX.Element => {
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [navigateOnOrderSubmit, setNavigateOnOrderSubmit] = useState<
    | {
        pathname: string;
        search?: string;
      }
    | string
  >('');

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    confirmOrder();
  }, []);

  // Retries to skip error during simulatneous verifone async IPN_trigger.
  // Verifone hotfix v1.6.4
  function fetchRetry(postUrl: any, retries = 5, backoff = 5000) {
    return postAPICall(postUrl, null)
      .then((response) => {
        if (response && response.status === 200) {
          if (dispatch) {
            dispatch({
              type: 'CHECKOUT_UPDATE',
              payload: {
                lastOrderId: response?.data?.orderId,
              },
            });
          }
          processOrderResponse(response?.data);
          return;
        }

        if (retries > 0 && (response.status < 200 || response.status > 299)) {
          setTimeout(() => {
            return fetchRetry(postUrl, retries - 1, backoff);
          }, backoff);
        } else {
          console.log(
            'Error during order processing - Retry failed with bad response',
            response.status
          );
          setErrorMessage(fields?.PaymentProcessErrorMessage?.value);
        }
      })
      .catch(function (error) {
        if (retries > 0) {
          setTimeout(() => {
            return fetchRetry(postUrl, retries - 1, backoff);
          }, backoff);
        } else {
          console.log('Error during order processing', error);
          setErrorMessage(fields?.PaymentProcessErrorMessage?.value);
        }
      });
  }

  function confirmOrder() {
    let search = window.location.search;
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('order-ext-ref');
    let isMonogram = orderId?.toLocaleUpperCase().endsWith(MONOGRAM_VERIFONE_SUBSCRIPTION_SUFFIX);
    let navigationPath = isMonogram === true ? 'orderconfirmed' : 'unifiedorderconfirmed';

    fetchRetry(`${process.env.REACT_APP_CART_URL}/external/${navigationPath}${search}`);
  }

  function processOrderResponse(orderResponse: OrderResponse) {
    if (orderResponse) {
      if (dispatch) {
        dispatch({
          type: 'CART_UPDATE',
          payload: {
            toggleCartQuantityLoad: !state?.toggleCartQuantityLoad,
          },
        });
      }
      let isMonogram = orderResponse?.orderId
        .toLocaleUpperCase()
        .endsWith(MONOGRAM_VERIFONE_SUBSCRIPTION_SUFFIX);
      let navigationPath = isMonogram === true ? '/monogrampayment' : '/orders';

      const { responseStatus, isGroupInvoice } = orderResponse;

      if (isGroupInvoice) {
        switch (responseStatus) {
          case 'Confirmed':
            setNavigateOnOrderSubmit({
              pathname: '/payment/confirmation',
              search: `?invoiceNumber=${orderResponse?.orderId}`,
            });
            break;
          case 'Pending':
            setNavigateOnOrderSubmit({
              pathname: '/payment/pending',
              search: `?invoiceNumber=${orderResponse?.orderId}`,
            });
            break;
          default:
            setNavigateOnOrderSubmit({
              pathname: '/payment/unsuccessful',
            });
        }
      } else {
        switch (responseStatus) {
          case 'Confirmed':
            setNavigateOnOrderSubmit(`${navigationPath}/confirmation`);
            break;
          case 'Pending':
            setNavigateOnOrderSubmit(`${navigationPath}/pending`);
            break;
          default:
            setNavigateOnOrderSubmit(`${navigationPath}/unsuccessful`);
        }
      }
    }
  }

  if (fields) {
    return (
      <div className="externalpaymentprocessing">
        {errorMessage && errorMessage.length > 0 ? (
          <RichText field={fields.PaymentProcessErrorMessage} />
        ) : (
          <>
            <RichText className="paymentRichText" field={fields.PaymentProcessMessage} />
            <LoadingSection open={true} onClick={() => {}} size="fullSize" message="" />
            {navigateOnOrderSubmit ? <Navigate to={navigateOnOrderSubmit} /> : null}
          </>
        )}
      </div>
    );
  } else if (!fields && sitecoreContext.pageEditing) {
    return <p>Please setup the Datasource.</p>;
  }
  return <></>;
};

export default withSitecoreContext()(ExternalPaymentProcessing);
